import loadable from '@loadable/component';
import { toPercentage } from '@/utilities/formatting';
import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import ReportSectionHeader from '@/components/ReportSectionHeader';
import {
  PORTFOLIO_STATIC_COMPANY,
  PORTFOLIO_FUTURE_GROWTH_ANALYSIS,
} from '@/constants/feedbackSections';
import { PORTFOLIO_DOWNLOAD_FUTURE_GROWTH } from '@/constants/downloadSections';
import { getPortfolioInfographicDownloadUrl } from '@utilities/urlGenerator';
import DeferRenderUntilIntersecting from '@/components/DeferRenderUntilIntersecting';

const ChartActionsContainer = loadable(
  () => import('@/components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);
const FutureAnnualGrowthAnalysis = loadable(
  () => import('@/components/DeprecatedPortfolio/components/Future'),
  {
    ssr: false,
    resolveComponent: (component) => component.FutureAnnualGrowthAnalysis,
  }
);

export interface Props {
  portfolioId: string;
  portfolioName: string;
  secretCode?: string;
  sectionNumber: number;
  marketName: string;
  expectedAnnualEarningsGrowthRate?: number;
  pastAverageEarningsGrowthRate?: number;
  marketEarningsGrowthRate?: number;
  expectedRevenueGrowthRate?: number;
  pastAverageRevenueGrowthRate?: number;
  marketRevenueGrowthRate?: number;
  expectedAnnualEarningsGrowthRateFormatted: string;
  pastAverageEarningsGrowthRateFormatted: string;
  marketEarningsGrowthRateFormatted: string;
  expectedRevenueGrowthRateFormatted: string;
  pastAverageRevenueGrowthRateFormatted: string;
  marketRevenueGrowthRateFormatted: string;
}

const calculateKeyMetric = (
  expectedAnnualEarningsGrowthRate: number,
  expectedRevenueGrowthRate: number
) => {
  if (expectedAnnualEarningsGrowthRate) {
    return {
      keymetricTitle: 'Expected Annual Earnings Growth',
      value: toPercentage(expectedAnnualEarningsGrowthRate / 100, 2),
    };
  } else if (expectedRevenueGrowthRate) {
    return {
      keymetricTitle: 'Expected Annual Revenue Growth',
      value: toPercentage(expectedRevenueGrowthRate / 100, 2),
    };
  } else {
    return {};
  }
};

const FuturePerformance = ({
  portfolioId,
  portfolioName,
  secretCode,
  sectionNumber,
  marketName,
  expectedAnnualEarningsGrowthRate,
  pastAverageEarningsGrowthRate,
  marketEarningsGrowthRate,
  expectedRevenueGrowthRate,
  pastAverageRevenueGrowthRate,
  marketRevenueGrowthRate,
  expectedAnnualEarningsGrowthRateFormatted,
  pastAverageEarningsGrowthRateFormatted,
  marketEarningsGrowthRateFormatted,
  expectedRevenueGrowthRateFormatted,
  pastAverageRevenueGrowthRateFormatted,
  marketRevenueGrowthRateFormatted,
}: Props) => (
  <ReportSection sectionNumber={sectionNumber}>
    <ReportSectionHeader
      title="Future Growth"
      sectionDescription="The expected growth in revenue and earnings over the next 1 to 3 years"
      keyMetricTitle={
        calculateKeyMetric(
          expectedAnnualEarningsGrowthRate as number,
          expectedRevenueGrowthRate as number
        ).keymetricTitle
      }
      keyMetric={
        calculateKeyMetric(
          expectedAnnualEarningsGrowthRate as number,
          expectedRevenueGrowthRate as number
        ).value
      }
    />
    <ReportSubSection
      title="Future Annual Growth Analysis"
      trackingProps={{ subject: 'chart' }}
      body={
        <DeferRenderUntilIntersecting preRenderHeight={302}>
          {() => (
            <>
              <FutureAnnualGrowthAnalysis
                marketName={marketName}
                portfolioName={portfolioName}
                expectedAnnualEarningsGrowthRate={
                  expectedAnnualEarningsGrowthRate as number
                }
                pastAverageEarningsGrowthRate={
                  pastAverageEarningsGrowthRate as number
                }
                marketEarningsGrowthRate={marketEarningsGrowthRate as number}
                expectedRevenueGrowthRate={expectedRevenueGrowthRate as number}
                pastAverageRevenueGrowthRate={
                  pastAverageRevenueGrowthRate as number
                }
                marketRevenueGrowthRate={marketRevenueGrowthRate as number}
                expectedAnnualEarningsGrowthRateFormatted={
                  expectedAnnualEarningsGrowthRateFormatted
                }
                pastAverageEarningsGrowthRateFormatted={
                  pastAverageEarningsGrowthRateFormatted
                }
                marketEarningsGrowthRateFormatted={
                  marketEarningsGrowthRateFormatted
                }
                expectedRevenueGrowthRateFormatted={
                  expectedRevenueGrowthRateFormatted
                }
                pastAverageRevenueGrowthRateFormatted={
                  pastAverageRevenueGrowthRateFormatted
                }
                marketRevenueGrowthRateFormatted={
                  marketRevenueGrowthRateFormatted
                }
              />
              <ChartActionsContainer
                companyName={PORTFOLIO_STATIC_COMPANY}
                feedbackLabel={PORTFOLIO_FUTURE_GROWTH_ANALYSIS}
                exportDarkUrl={
                  getPortfolioInfographicDownloadUrl(
                    portfolioId,
                    PORTFOLIO_DOWNLOAD_FUTURE_GROWTH,
                    secretCode || ''
                  ) || ''
                }
              />
            </>
          )}
        </DeferRenderUntilIntersecting>
      }
    />
  </ReportSection>
);

FuturePerformance.defaultProps = {
  expectedAnnualEarningsGrowthRate: 0,
  pastAverageEarningsGrowthRate: 0,
  marketEarningsGrowthRate: 0,
  expectedRevenueGrowthRate: 0,
  pastAverageRevenueGrowthRate: 0,
  marketRevenueGrowthRate: 0,
};

export default FuturePerformance;
